import { Component, Input, SimpleChanges, OnInit } from '@angular/core';

import { Hospital } from '../hospital';

import { FormGroup, FormControl, FormBuilder, FormArray } from '@angular/forms';

import { FlashService } from '../../components/flash/flash.service';
import { HospitalService } from '../hospital.service';
import { FederatedIdentityGroupService } from '../../federated-identity-groups/federated-identity-group.service';

import { forkJoin } from 'rxjs';
import * as _ from 'lodash';


@Component({
    selector: 'app-quicksight-reports',
    templateUrl: './quicksight-reports.component.html',
    styleUrls: ['./quicksight-reports.component.scss'],
    standalone: false
})
export class QuicksightReportComponent implements OnInit {
  @Input() hospital: Hospital;
  quicksightReportForm: FormGroup;
  editingMapping = false;

  // All possible reports
  reportMapping: { id: number; report_name: string; enabled: boolean }[] = [];

  // Join-table rows + currently enabled IDs
  hospitalReportRows: { id: number; hospital_id: number; quicksight_report_id: number }[] = [];
  hospitalReportIds: number[] = [];

  careCoordinatorRoleId: number | null = null;

  constructor(
    private flashService: FlashService,
    protected hospitalService: HospitalService,
    private federatedIdentityGroupService: FederatedIdentityGroupService,
    private fb: FormBuilder
  ) {
      this.quicksightReportForm = this.fb.group({
        reports: this.fb.array([])
      })
  }

  get reportsArray(): FormArray {
    return this.quicksightReportForm.get('reports') as FormArray;
  }

  ngOnInit() {
    this.loadReports();
  }

  private loadReports() {
    // 1) First fetch roles to get care_coordinator role ID
    this.federatedIdentityGroupService.getRoles().subscribe({
      next: (rolesResponse) => {
        // Find care_coordinator role ID
        const careCoordinatorRole = rolesResponse.roles?.find(role => role.code?.toLowerCase() === 'care_coordinator');
        this.careCoordinatorRoleId = careCoordinatorRole?.id || null;

        if (!this.careCoordinatorRoleId) {
          this.flashService.add({
            type: 'warning',
            message: '<strong>Warning!</strong> Care Coordinator role not found.'
          });
          return;
        }

        // 2) Fetch reports filtered by care_coordinator role and hospital reports in parallel
        forkJoin({
          allReports: this.hospitalService.getAllReports(this.careCoordinatorRoleId),
          hospitalReports: this.hospitalService.getHospitalReports(this.hospital.id)
        }).subscribe({
          next: ({ allReports, hospitalReports }) => {
            // Store hospital report rows and IDs
            this.hospitalReportRows = hospitalReports || [];
            this.hospitalReportIds = this.hospitalReportRows.map(r => r.quicksight_report_id);

            // Build mapping (only care_coordinator reports + enabled flag)
            const allReportsData = allReports.quicksight_reports || [];
            this.reportMapping = allReportsData.map(r => ({
              id: r.id,
              report_name: r.report_name,
              enabled: this.hospitalReportIds.includes(r.id)
            }));

            // Rebuild form from mapping
            this.rebuildForm();
            this.editingMapping = false;
          },
          error: (err) => {
            this.flashService.add({
              type: 'danger',
              message: '<strong>Error!</strong> Failed to load reports.'
            });
            console.error('Error loading reports:', err);
          }
        });
      },
      error: (err) => {
        this.flashService.add({
          type: 'danger',
          message: '<strong>Error!</strong> Failed to load roles.'
        });
        console.error('Error loading roles:', err);
      }
    });
  }

  private rebuildForm() {
    this.reportsArray.clear();

    for (const report of this.reportMapping) {
      this.reportsArray.push(
        new FormGroup({
          value: new FormControl(report.enabled)
        })
      );
    }

    this.disableFormControls();
  }

  editMapping() {
    this.editingMapping = true;
    this.enableFormControls();
  }

  cancelEditing() {
    this.editingMapping = false;
    this.rebuildForm();
  }

  update() {
    const formValues = this.reportsArray.value as { value: boolean }[];

    const newEnabledIds = formValues
      .map((row, index) => row.value ? this.reportMapping[index].id : null)
      .filter((id): id is number => id !== null);

    const oldEnabledIds = this.hospitalReportIds;

    const added = newEnabledIds.filter(id => !oldEnabledIds.includes(id));
    const removed = oldEnabledIds.filter(id => !newEnabledIds.includes(id));

    const rowByReportId = new Map(
      this.hospitalReportRows.map(r => [r.quicksight_report_id, r.id])
    );

    const addCalls = added.map(id =>
      this.hospitalService.createHospitalReport(this.hospital.id, id)
    );

    const deleteCalls = removed.map(id =>
      this.hospitalService.deleteHospitalReport(rowByReportId.get(id) as number)
    );

    if (addCalls.length === 0 && deleteCalls.length === 0) {
      this.editingMapping = false;
      this.disableFormControls();
      return;
    }

    forkJoin([...addCalls, ...deleteCalls]).subscribe({
      next: () => {
        this.flashService.add({
          type: 'success',
          message: '<strong>Success!</strong> Associated Reports have been updated.'
        });

        this.editingMapping = false;
        this.loadReports(); // reload fresh state
      },
      error: () => {
        this.flashService.add({
          type: 'danger',
          message: '<strong>Oops!</strong> There was a problem updating reports.'
        });

        this.editingMapping = false;
        this.loadReports();
      }
    });
  }

  private disableFormControls() {
    this.reportsArray.controls.forEach(control => control.disable());
  }

  private enableFormControls() {
    this.reportsArray.controls.forEach(control => control.enable());
  }
}

